export type PageResponse<T> = {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
};

export type TakeLabel = {
  canonicalName: string;
  prettyName: string;
  sortOrder: number;
};

export type Take = {
  takeId: string;
  userArrangementId: string;
  practiceSessionId: string | null;
  label: TakeLabel;
  recordedAt: string;
  durationSeconds: number;
  tempoBpm: number | null;
  notes: string | null;
  audioUrl: string;
  mimeType: string;
  fileSizeBytes: number;
  createdAt: string;
};

export type RepertoireEntry = {
  userArrangementId: string;
  arrangementId: string;
  arrangementName: string;
  pieceId: string | null;
  pieceName: string;
  masteryLevelId: string | null;
  masteryLevelName: string | null;
  queueStatusId: string;
  queueStatusName: string;
  isActive: boolean;
  snoozedUntil: string | null;
  keyId: string | null;
  keyName: string | null;
  timeSignatureId: string | null;
  timeSignatureName: string | null;
  formId: string | null;
  formName: string | null;
  practiceTempoBeats: number | null;
  performanceTempoBeats: number | null;
  notes: string | null;
  firstPracticeOn: string | null;
};

export type ReferenceItem = {
  prettyName: string;
  canonicalName: string;
};

export type MasteryLevel = ReferenceItem & { masteryLevelId: string; sortOrder: number };
export type QueueStatus = ReferenceItem & { queueStatusId: string; sortOrder: number };

// Mirrors the backend repertoire.region_kind enum (PieceRegion).
export const PIECE_REGIONS = [
  'IRELAND',
  'SCOTLAND',
  'CAPE_BRETON',
  'BRITTANY',
  'ENGLAND',
  'GALICIA',
  'ASTURIAS',
  'OTHER',
] as const;
export type PieceRegion = (typeof PIECE_REGIONS)[number];

export type Piece = {
  pieceId: string;
  prettyName: string;
  canonicalName: string;
  composerId: string | null;
  composerName: string | null;
  // Present on list and contribute responses; empty for legacy/plain pieces.
  // Optional so existing fixtures that pre-date the field still type-check.
  forms?: string[];
  // Present on moderation detail responses; optional so existing fixtures still type-check.
  region?: PieceRegion | null;
  summary?: string | null;
  aka?: string[];
  // Present on list responses (read model); absent on detail responses.
  arrangementCount?: number;
  inRepertoire?: boolean;
};

export type PieceAlias = {
  pieceAliasId: string;
  name: string;
};

export type Arrangement = {
  arrangementId: string;
  pieceId: string;
  pieceName: string;
  prettyName: string;
  canonicalName: string;
  arrangerId: string | null;
  arrangerName: string | null;
  keyId: string | null;
  keyName: string | null;
  timeSignatureId: string | null;
  timeSignatureName: string | null;
  formId: string | null;
  formName: string | null;
};

export type Key = ReferenceItem & { keyId: string; tonic: string; mode: string };
export type Form = ReferenceItem & { formId: string };
export type TimeSignature = ReferenceItem & { timeSignatureId: string };
export type Instrument = ReferenceItem & { instrumentId: string };
export type AssessmentLevel = ReferenceItem & { assessmentLevelId: string; sortOrder: number };

export type QueueItem = {
  userArrangementId: string;
  arrangementId: string;
  arrangementName: string;
  pieceName: string;
  composerName: string | null;
  keyName: string | null;
  formName: string | null;
  masteryLevelName: string | null;
  masteryLevelSortOrder: number;
  queueStatusName: string;
  pinned: boolean;
  dueNow: boolean;
  lastPracticedAt: string | null;
  daysSinceLastPracticed: number;
  lastAssessmentName: string | null;
  notes: string | null;
  plannedMinutes: number | null;
  partsCount: number | null;
  structurePattern: string | null;
  practiceTempoBeats: number | null;
};

export type PracticeQueue = {
  focusPieces: QueueItem[];
  maintenancePieces: QueueItem[];
};

export type PracticeHistoryEntry = {
  practiceSessionId: string;
  practicedAt: string;
  instrumentId: string;
  instrumentName: string;
  assessmentLevelId: string | null;
  assessmentLevelName: string | null;
  durationMinutes: number | null;
  notes: string | null;
};

export type Comment = {
  commentId: string;
  userId: string;
  content: string;
};

export type Genre = {
  arrangementGenreId: string;
  genreId: string;
  genreName: string;
};

export type DashboardStats = {
  streakDays: number;
  sessionsLast7Days: number;
  totalRepertoirePieces: number;
  averageSessionDurationMinutes: number;
  masteryBreakdown: Record<string, number>;
};

export type HeatmapDay = {
  date: string;
  sessionCount: number;
  totalMinutes: number;
};

export type HeatmapData = {
  startDate: string;
  endDate: string;
  activity: HeatmapDay[];
};

export type RustyPiece = {
  userArrangementId: string;
  arrangementId: string;
  arrangementName: string;
  pieceName: string;
  masteryLevelName: string | null;
  lastPracticedAt: string | null;
  daysSinceLastPracticed: number;
};

export type ArrangementResource = {
  arrangementResourceId: string;
  arrangementId: string;
  resourceType: string;
  sourceType: string;
  title: string;
  url: string | null;
  downloadUrl: string | null;
  mimeType: string | null;
  fileSizeBytes: number | null;
};

export type Mood = 'OFF' | 'MEH' | 'GOOD' | 'FLOW';

export type TuneMasteryDelta = {
  userArrangementId: string;
  arrangementName: string;
  beforeSortOrder: number;
  afterSortOrder: number;
  levelled: boolean;
};

export type SessionSummary = {
  practiceSessionId: string;
  durationMinutes: number | null;
  notes: string | null;
  mood: Mood | null;
  practicedAt: string;
  streakDays: number;
  tunesPracticed: number;
  takesCount: number;
  perTuneMasteryDelta: TuneMasteryDelta[];
};
